import { Box, Flex, Text, Spinner, Icon, IconButton, Tooltip, useDisclosure } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { IconTrashbin } from 'assets/icons';
import Button from 'components/Button';
import { ConfirmationModal } from 'components/Modal';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import { customPaddingStyles } from 'containers/Integrations/shared/react-select-custom';
import { useDeleteRipplingEmployee } from 'hooks/integrations/useDeleteRipplingEmployee';
import { fetchRipplingPaginatedEarningTypes } from 'hooks/integrations/useFetchPaginatedEarningTypes';
import {
  useFetchRipplingPaginatedEmployee,
  IRipplingEmployeeSearchQuery,
} from 'hooks/integrations/useFetchPaginatedRipplingEmployees';
import { fetchPaginatedRipplingTimeeroEmployees } from 'hooks/integrations/useFetchPaginatedRipplingTimeeroEmployees';
import { useImportRipplingEmployees, IImportRipplingEmployees } from 'hooks/integrations/useImportRipplingEmployees';
import { useMapRipplingEmployee, IRipplingEmployee } from 'hooks/integrations/useMapRipplingEmployee';
import { useMapRipplingEarnings, IRipplingEarnings } from 'hooks/integrations/useMapRipplingMileageEarning';
import { useSyncRippling } from 'hooks/integrations/useSyncRippling';
import {
  useUnmapRipplingTimeeroEmployee,
  IRipplingUnmapTimeeroEmployee,
} from 'hooks/integrations/useUnMapRipplingEmployee';
import { getAsyncSelectLoadOptions } from 'utils/helpers';
import toaster from 'utils/toaster';

import RipplingMappingTableFooter from './RipplingMappingTableFooter';

const initialSearchQuery: IRipplingEmployeeSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const RipplingMapping = () => {
  const [mappedUsers, setMappedUsers] = useState<any>(null);
  const [dialogId, setDialogId] = useState<number | null>(null);
  const [currentMapUser, setCurrentMapUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IRipplingEmployeeSearchQuery>(initialSearchQuery);

  const { t } = useTranslation(['common', 'integrations', 'jobsTasks']);

  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const {
    data: ripplingEmployeeData,
    status: ripplingEmployeeStatus,
    refetch,
  } = useFetchRipplingPaginatedEmployee(searchQuery);
  const { status: ripplingEmployeeMapStatus, mutateAsync: ripplingEmployeeMapMutateAsync } = useMapRipplingEmployee();
  const { status: ripplingUnmapEmployeeStatus, mutateAsync: ripplingUnmapEmployeeMutateAsync } =
    useUnmapRipplingTimeeroEmployee();
  const { status: ripplingImportEmployeesStatus, mutateAsync: ripplingImportEmployeesMutateAsync } =
    useImportRipplingEmployees();
  const { status: ripplingMapEarningStatus, mutateAsync: ripplingMapEarningMutateAsync } = useMapRipplingEarnings();
  const { status: ripplingSyncStatus, mutateAsync: ripplingSyncMutateAsync } = useSyncRippling();
  const { status: ripplingDeleteStatus, mutateAsync: ripplingDeleteMutateAsync } = useDeleteRipplingEmployee();

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
      },
    },
    {
      Header: t('integrations:rippling.ripplingUsers'),
      accessor: 'name',
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.name}</Text>;
      },
    },
    {
      Header: t('integrations:mileageEarnings'),
      accessor: 'role',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <Box position="relative" width="100%">
            {((ripplingMapEarningStatus === 'loading' && currentMapUser === data.row.original.id) ||
              ripplingEmployeeStatus === 'loading') && (
              <Flex
                width="100%"
                height="100%"
                position="absolute"
                zIndex={2}
                justifyContent="center"
                alignItems="center"
                background="rgba(255, 255, 255, 0.7)"
              >
                <Spinner size="xs" />
              </Flex>
            )}
            <ReactSelectAsyncPaginate
              loadOptions={earningTypesLoadOptions}
              additional={{
                page: 1,
              }}
              value={
                data.row.original?.selected_mileage_earning
                  ? { name: data.row.original?.selected_mileage_earning }
                  : mappedUsers &&
                      mappedUsers[`${data.row.original.id}`] &&
                      mappedUsers[`${data.row.original.id}`].earning
                    ? mappedUsers[`${data.row.original.id}`].earning
                    : null
              }
              onChange={(value: any) => {
                setCurrentMapUser(data.row.original.id);
                const postData: IRipplingEarnings = {
                  name: value.name,
                  rippling_employee_id: data.row.original.id,
                };
                ripplingMapEarningMutateAsync(postData).then(() => {
                  refetch();
                });
              }}
              size="md"
              variant="filled"
              fontSize="sm"
              width="100%"
              debounceTimeout={300}
              menuPortalTarget={document.body}
              isSearchable
              placeholder={t('integrations:selectEarning')}
              getOptionLabel={(option: any) => option.name}
              getOptionValue={(option: any) => option.name}
            />
          </Box>
        );
      },
    },
    {
      Header: t('integrations:timeeroUsers'),
      accessor: 'timeeroUsers',
      width: 240,
      Cell: (data: CellValue) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={timeeroEmployeeLoadOptions}
            additional={{
              page: 1,
            }}
            value={
              data.row.original?.selected_user
                ? data.row.original?.selected_user || null
                : mappedUsers &&
                    mappedUsers[`${data.row.original.id}`] &&
                    mappedUsers[`${data.row.original.id}`].employee
                  ? mappedUsers[`${data.row.original.id}`].employee
                  : null
            }
            onChange={(value: any) => {
              setMappedUsers((prevValue: any) => ({
                ...prevValue,
                [data.row.original.id]: {
                  employee: value || null,
                },
              }));
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            debounceTimeout={300}
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('common:selectUser')}
            styles={customPaddingStyles}
            formatOptionLabel={formatOptionLabel}
            getOptionValue={(option: any) => option.userId}
          />
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      width: 220,
      Cell: (data: CellValue) => {
        return (
          <>
            {data.row.original.selected_user ? (
              <Button
                _hover={{ bg: 'secondary.100' }}
                fontWeight="bold"
                background="rgba(250, 85, 85, 0.2)"
                color="rgba(250, 85, 85, 1)"
                isLoading={ripplingUnmapEmployeeStatus === 'loading' && currentMapUser === data.row.original.id}
                onClick={() => onUnMapEmployees(data.row.original.id, data.row.original.selected_user.id)}
              >
                {t('integrations:removeMapping')}
              </Button>
            ) : (
              <Button
                _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
                background="rgba(255, 103, 32, 0.1)"
                colorScheme="rgba(255, 103, 32, 0.3)"
                color="secondary.500"
                isLoading={ripplingEmployeeMapStatus === 'loading' && currentMapUser === data.row.original.id}
                onClick={() => onMapEmployees(data.row.original.id)}
              >
                {t('integrations:mapUser')}
              </Button>
            )}
            <Tooltip label={t('integrations:rippling.deleteRipplingUser')} hasArrow bg="black">
              <IconButton
                aria-label="Delete Rippling User"
                bg="rgba(255, 83, 83, 0.1)"
                icon={<Icon as={IconTrashbin} />}
                ml="2"
                // size="md"
                _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                _active={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                onClick={() => handleDeleteRipplingIconClick(data.row.original.id)}
              />
            </Tooltip>
          </>
        );
      },
    },
  ];

  const formatOptionLabel = (option: any) => {
    return (
      <Box>
        <Text fontSize="sm" fontWeight={600}>
          {option.firstName + ' ' + option.lastName}
        </Text>
        <Text fontSize="sm" fontWeight={400}>
          {option.email}
        </Text>
      </Box>
    );
  };

  const handleDeleteRipplingIconClick = (id: number) => {
    setDialogId(id);
    onDeleteModalOpen();
  };

  const deleteRipplingEmployee = () => {
    ripplingDeleteMutateAsync({ rippling_employee_id: dialogId! }).then(() => {
      setDialogId(null);
      onDeleteModalClose();
      refetch();
    });
  };

  const onMapEmployees = (ripplingId: number) => {
    if (mappedUsers && mappedUsers[ripplingId] && mappedUsers[ripplingId].hasOwnProperty('employee')) {
      setCurrentMapUser(ripplingId);
      const postData: IRipplingEmployee = {
        rippling_employee_id: ripplingId,
        user_id: parseInt(mappedUsers[ripplingId].employee.id),
      };
      ripplingEmployeeMapMutateAsync(postData).then(() => {
        refetch();
      });
    } else {
      toaster.error({ description: t('integrations:employeeSelectMapError') });
    }
  };

  const onUnMapEmployees = (ripplingId: number, timeeroEmployeeId: number) => {
    setCurrentMapUser(ripplingId);
    const postData: IRipplingUnmapTimeeroEmployee = {
      timeero_employee_id: timeeroEmployeeId,
    };
    ripplingUnmapEmployeeMutateAsync(postData).then(() => {
      setCurrentMapUser(null);
      setMappedUsers((prevValue: any) => ({
        ...prevValue,
        [ripplingId]: null,
      }));
      refetch();
    });
  };

  const onImportJobs = (selectedIds: Array<number>) => {
    const postData: IImportRipplingEmployees = {
      rippling_employee_ids: selectedIds,
    };
    ripplingImportEmployeesMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const onSyncRippling = () => {
    ripplingSyncMutateAsync().then(() => {
      refetch();
    });
  };

  const earningTypesLoadOptions = async (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchRipplingPaginatedEarningTypes, { search, page });
  };

  const timeeroEmployeeLoadOptions = async (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedRipplingTimeeroEmployees, { search, page });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IRipplingEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    const updatedSearchQuery = (previousSearchQuery: IRipplingEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page_size: pageSize,
      page: 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <Box>
      <Title>{t('integrations:rippling.ripplingMapping')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={ripplingSyncStatus === 'loading'}
          onClick={onSyncRippling}
        >
          {t('integrations:rippling.syncChanges')}
        </Button>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: t('common:searchUsers'),
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('integrations:rippling.confirmDeleteUser')}
        subTitle=""
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          _active: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: ripplingDeleteStatus === 'loading',
          onClick: deleteRipplingEmployee,
        }}
      />

      <Table
        columns={columns}
        data={ripplingEmployeeData?.payload || []}
        isLoading={ripplingEmployeeStatus === 'loading'}
        enableRowSelect
        paginationData={ripplingEmployeeData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <RipplingMappingTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleImportJobs={onImportJobs}
              isLoading={ripplingImportEmployeesStatus === 'loading'}
              t={t}
            />
          );
        }}
      />
    </Box>
  );
};
export default RipplingMapping;
